import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { useParams } from 'react-router-dom';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import {
  PiQuestionMarkLight,
  PiPlusLight,
  PiMagnifyingGlassLight,
  PiCheckCircleLight,
  PiClockLight,
  PiUserPlusLight,
  PiFileTextLight,
  PiArrowLeftLight,
  PiSparkleLight
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import { finalizeQuestionnaireItem, restoreQuestionnaireItem } from '../../../store/actions/Matters/MatterActions';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import Tooltip from '@mui/material/Tooltip';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, qasItemsTourSteps } from '../../shared/FeatureTour';

interface QAsTabContentProps {
  matterData: MatterData | null;
  questionnaire: any[];
  loading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

const QAsTabContent: React.FC<QAsTabContentProps> = ({
  matterData,
  questionnaire,
  loading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const matterUuid = matterData?.uuid || matterData?.MatterID || '';
  
  const [activeQATab, setActiveQATab] = useState<'unanswered' | 'answered' | 'completed'>('unanswered');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchTerm, setSearchTerm] = useState('');

  const { isGenerating: isFindingMore, handleGenerateMore: handleFindMoreQuestions, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'questions',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'qas-items',
    steps: qasItemsTourSteps,
    readyToShow: questionnaire.length > 0 && !loading,
    autoStartDelay: 600,
  });

  const getStatusColor = (status: string, hasAnswer: boolean = false): BadgeColor => {
    if (status === 'finalized') return 'green';
    if (status === 'pending' && hasAnswer) return 'blue';
    return 'yellow'; // pending without answer
  };

    const filteredQuestionnaire = useMemo(() => {
    let filtered = questionnaire.filter((question: any) => {
        const matchesSearch = question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (question.potential_answer && question.potential_answer.toLowerCase().includes(searchTerm.toLowerCase()));

        switch (activeQATab) {
        case 'unanswered':
            return matchesSearch && question.status === 'pending' && !question.potential_answer;
        case 'answered':
            return matchesSearch && question.status === 'answered';
        case 'completed':
            return matchesSearch && question.status === 'finalized';
        default:
            return matchesSearch;
        }
    });

    filtered.sort((a: any, b: any) => {
        const aDate = new Date(a.when_answered || a.created_at || '1970-01-01').getTime();
        const bDate = new Date(b.when_answered || b.created_at || '1970-01-01').getTime();
        return bDate - aDate;
    });

    return filtered;
    }, [questionnaire, activeQATab, searchTerm]);

    const qaTabs = [
    {
        id: 'unanswered',
        label: t('Unanswered'),
        icon: <PiClockLight className="h-3 w-3" />,
        count: questionnaire.filter((q: any) => q.status === 'pending' && !q.potential_answer).length,
        color: 'yellow' as BadgeColor
    },
    {
        id: 'answered',
        label: t('Answered'),
        icon: <PiCheckCircleLight className="h-3 w-3" />,
        count: questionnaire.filter((q: any) => q.status === 'answered').length,
        color: 'green' as BadgeColor
    },
    {
        id: 'completed',
        label: t('Completed'),
        icon: <PiFileTextLight className="h-3 w-3" />,
        count: questionnaire.filter((q: any) => q.status === 'finalized').length,
        color: 'blue' as BadgeColor
    },
    ];

  const handleAddQuestion = () => {
    if (!canEdit) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t("You don't have access to add a question."),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }

    const matterUuid = matterData?.uuid || matterData?.MatterID || '';
    dispatch(showSidePanel({
      type: 'questionnairePanel',
      matterId: matterUuid,
      questionIndex: -1, 
      onClose: () => {},
      onUpdate: onUpdate,
      showSidePanel: true,
      width: '50%',
      message: t('Add New Question'),
      subMsg: t('Create a new question for the questionnaire.'),
      icon: <PiQuestionMarkLight className="w-6 h-6 text-gray-600" />,
    }));
  };

  const handleOpenQuestionnaire = (item: any, index: number) => {
    const matterUuid = matterData?.uuid || matterData?.MatterID || '';
    dispatch(showSidePanel({
      type: 'questionnairePanel',
      matterId: matterUuid,
      questionIndex: index,
      item,
      onClose: () => {},
      onUpdate: onUpdate,
      showSidePanel: true,
      width: '50%',
      message: t('Questions & Answers'),
      subMsg: t('Answer your question here.'),
      icon: <PiQuestionMarkLight className="w-6 h-6 text-gray-600" />,
    }));
  };


  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusDisplay = (status: string, hasAnswer: boolean = false) => {
    if (status === 'finalized') return t('Completed');
    if (status === 'pending' && hasAnswer) return t('Answered');
    if (status === 'pending') return t('Pending');
    return status;
  };

const QuestionCard = ({ question, index, userAccessLevel: userAccessLevelParam,  onUpdate, handleOpenQuestionnaire }: { question: any; index: number; userAccessLevel: 'edit' | 'admin' | 'view'; matterData: MatterData | null; onUpdate: () => void; handleOpenQuestionnaire: (item: any, index: number) => void }) => {
  const { matterId } = useParams<{ matterId: string }>();
  const isAnswered = !!question.potential_answer;
  const isFinalized = question.status === 'finalized';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevelParam === 'edit' || userAccessLevelParam === 'admin';

  const handleRestoreQuestion = async () => {
    if (!matterId || !question.question_id) return;

    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Restore Question'),
      subMsg: t('This will move the question back to Answered status and allow further editing.'),
      onConfirm: async () => {
        try {
          await dispatch(restoreQuestionnaireItem(matterId, question.question_id));
          onUpdate();
        } catch (error) {
        }
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      icon: <PiArrowLeftLight className="w-6 h-6 text-yellow-600" />,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "group rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out overflow-hidden",
        isAnswered && "border-green-200 dark:border-green-700 hover:border-green-300",
        isFinalized && "border-blue-200 dark:border-blue-700 hover:border-blue-300"
      )}
    >
      <div className="relative p-4">
        <h4 
          className="text-sm text-gray-900 dark:text-gray-100 mb-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          title={question.question}
          onClick={() => handleOpenQuestionnaire(question, index)}
        >
          {question.question}
        </h4>
      </div>

      <div className="min-h-[80px]">
        {isAnswered ? (
          <div className="px-4 pb-4">
            <div className="bg-gray-50/80 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200/50 dark:border-gray-600/50">
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap line-clamp-3">
                {question.potential_answer}
              </p>
              
              {(question.who_answered || question.when_answered) && (
                <div className="flex items-center gap-2 mt-2 pt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200/50 dark:border-gray-600/50">
                  <span className="text-gray-400">
                    {question.who_answered ? `${question.who_answered}` : t('You')}
                  </span>
                  {question.when_answered && (
                    <span>• {formatDate(question.when_answered)}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="px-4 pb-4 text-center py-6">
            <PiQuestionMarkLight className="mx-auto h-6 w-6 text-gray-400 mb-1" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('No answer yet')}
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700"></div>

      <div className='flex justify-between items-center px-4 py-3'>
        <Badge
          color={getStatusColor(question.status, isAnswered)}
          className="capitalize"
        >
          {getStatusDisplay(question.status, isAnswered)}
        </Badge>
        
        {question.client_assigned && (
          <div className="mt-1">
            <Badge color="indigo" className="text-xs px-2 py-0.5">
              <PiUserPlusLight className="h-3 w-3 mr-1 inline" />
              {t('Client')}
            </Badge>
          </div>
        )}
        
        <div className="flex justify-end gap-2 bg-gray-50/50 dark:bg-gray-800/50">
          {canEdit && (
            <>
              <Button
                variant="outline"
                size="small"
                onClick={() => handleOpenQuestionnaire(question, index)}
                title={t('Open')}
              >
                {t('Open')}
              </Button>
              
              {isAnswered && !isFinalized && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!matterId || !question.question_id) return;

                    dispatch(showModal({
                      showModal: true,
                      type: 'confirmation',
                      message: t('Mark as Complete'),
                      subMsg: t('This will finalize this question and mark it as complete.'),
                      onConfirm: async () => {
                        try {
                          await dispatch(finalizeQuestionnaireItem(matterId, question.question_id));
                          onUpdate();
                        } catch (error) {
                        }
                        dispatch(hideModal());
                      },
                      onCancel: () => dispatch(hideModal()),
                      icon: <PiCheckCircleLight className="w-6 h-6 text-green-600" />,
                    }));
                  }}
                  title={t('Mark as Complete')}
                >
                  {t('Complete')}
                </Button>
              )}

              {isFinalized && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestoreQuestion();
                  }}
                  title={t('Restore to Answered')}
                  className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                >
                  {t('Restore')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

  const renderCurrentTab = () => {
    const total = filteredQuestionnaire.length;

    if (total === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center rounded-xl p-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {activeQATab === 'unanswered' ? t('No unanswered questions') :
             activeQATab === 'answered' ? t('No answered questions') :
             t('No completed questions')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
            {activeQATab === 'unanswered' ? t('Get started by adding your first question to gather essential information.') :
             activeQATab === 'answered' ? t('All questions in this matter have been answered.') :
             t('Complete your questions to finalize this section.')}
          </p>
          {canEdit && activeQATab === 'unanswered' && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                icon={<PiPlusLight className="h-4 w-4" />}
                onClick={handleAddQuestion}
                size='small'
              >
                {t('Add First Question')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={handleFindMoreQuestions}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="">
        {viewMode === 'table' ? (
          <>
            {activeQATab === 'completed' ? (
              <div data-tour-id="qas-context-hint" className="px-2 py-2 bg-green-50 dark:bg-green-900/10">
                <p className="text-xs text-green-700 dark:text-green-300">
                  {t('These questions are included in the matter context for drafting and insights.')}
                </p>
              </div>
            ) : (
              <div data-tour-id="qas-context-hint" className="px-2 py-2 bg-gray-50 dark:bg-gray-800/30">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('Only completed questions are included in the matter context.')}
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <Table
                columns={['Question', 'Answer', 'Status', 'Actions']}
                data={filteredQuestionnaire.map((question: any, index: number) => ({
                  id: question.question_id || index,
                  'Question': question.question || '',
                  'Answer': question.potential_answer || '',
                  'Status': question.status || 'pending',
                  _originalQuestion: question,
                  _index: index,
                }))}
                renderCell={(column: string, value: any, row: any) => {
                  if (column === 'Question') {
                    return (
                      <Tooltip title={row['Question']} arrow placement="top-start" enterDelay={400}>
                        <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {row['Question']}
                        </div>
                      </Tooltip>
                    );
                  }
                  if (column === 'Answer') {
                    if (!row.Answer) {
                      return <span className="text-gray-400 dark:text-gray-500 italic">{t('No answer')}</span>;
                    }
                    return (
                      <Tooltip title={row.Answer} arrow placement="top-start" enterDelay={400}>
                        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                          {row.Answer}
                        </div>
                      </Tooltip>
                    );
                  }
                  if (column === 'Status') {
                    const question = row._originalQuestion;
                    const hasAnswer = !!question.potential_answer;
                    return (
                      <Badge
                        color={getStatusColor(question.status, hasAnswer)}
                        className="capitalize text-xs"
                      >
                        {getStatusDisplay(question.status, hasAnswer)}
                      </Badge>
                    );
                  }
                  return undefined;
                }}
                onRowClick={(row: any) => {
                  if (row._originalQuestion) {
                    handleOpenQuestionnaire(row._originalQuestion, row._index);
                  }
                }}
                actions={[
                  {
                    label: t('Open'),
                    icon: <PiQuestionMarkLight className="h-4 w-4" />,
                    onClick: (row: any) => {
                      if (row._originalQuestion) {
                        handleOpenQuestionnaire(row._originalQuestion, row._index);
                      }
                    }
                  },
                  {
                    label: t('Complete'),
                    icon: <PiCheckCircleLight className="h-4 w-4" />,
                    disabled: (row: any) => {
                      const question = row._originalQuestion;
                      return !canEdit || question.status === 'finalized';
                    },
                    onClick: (row: any) => {
                      const question = row._originalQuestion;
                      if (!matterData || !question?.question_id) return;
                      dispatch(showModal({
                        showModal: true,
                        type: 'confirmation',
                        message: t('Mark as Complete'),
                        subMsg: t('This will finalize this question and mark it as complete.'),
                        onConfirm: async () => {
                          try {
                            await dispatch(finalizeQuestionnaireItem(matterData.uuid || matterData.MatterID, question.question_id));
                            onUpdate();
                          } catch (error) {
                          }
                          dispatch(hideModal());
                        },
                        onCancel: () => dispatch(hideModal()),
                        icon: <PiCheckCircleLight className="w-6 h-6 text-green-600" />,
                      }));
                    }
                  },
                  {
                    label: t('Restore'),
                    icon: <PiArrowLeftLight className="h-4 w-4" />,
                    disabled: (row: any) => {
                      const question = row._originalQuestion;
                      return !canEdit || question.status !== 'finalized';
                    },
                    onClick: (row: any) => {
                      const question = row._originalQuestion;
                      if (!matterData || !question?.question_id) return;
                      dispatch(showModal({
                        showModal: true,
                        type: 'confirmation',
                        message: t('Restore Question'),
                        subMsg: t('This will move the question back to Answered status and allow further editing.'),
                        onConfirm: async () => {
                          try {
                            await dispatch(restoreQuestionnaireItem(matterData.uuid || matterData.MatterID, question.question_id));
                            onUpdate();
                          } catch (error) {
                          }
                          dispatch(hideModal());
                        },
                        onCancel: () => dispatch(hideModal()),
                        icon: <PiArrowLeftLight className="w-6 h-6 text-yellow-600" />,
                      }));
                    }
                  }
                ]}
                resizableColumns={true}
                storageKeyPrefix="qas-table"
                defaultColumnWidths={{
                  'Question': 300,
                  'Answer': 250,
                  'Status': 120,
                  'Actions': 48,
                }}
                max="max-h-[calc(100vh-230px)]"
                min="max-h-[calc(100vh-230x)]"
              />
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar-main">
            {filteredQuestionnaire.map((question, index) => (
              <QuestionCard key={question.question_id || index} question={question} index={index} userAccessLevel={userAccessLevel} matterData={matterData} onUpdate={onUpdate} handleOpenQuestionnaire={handleOpenQuestionnaire} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-48 rounded"></div>
          <div className="animate-fadePulse bg-gray-200 dark:bg-gray-700 h-8 w-24 rounded"></div>
        </div>
        <SkeletonClientItem viewMode="table" count={5} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        {t('Loading questionnaire...')}
      </div>
    );
  }

  return (
    <div className="space-y-2">

      <div data-tour-id="qas-sub-tabs">
      <SubTabBar
        tabs={qaTabs}
        activeTab={activeQATab}
        onTabChange={(id) => setActiveQATab(id as any)}
        actions={canEdit && !isFindingMore ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={handleAddQuestion}
            >
              {t('Add Question')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreQuestions}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {questionnaire.length === 0 ? t('Generate') : t('Find More')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {isFindingMore || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMore} />
      ) : (
        <div>
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            viewMode={viewMode}
            setViewMode={setViewMode}
            noParentPadding={true}
            noParentHeight={true}
            itemCount={filteredQuestionnaire.length}
            totalItemCount={questionnaire.length}
            itemCountLabel={questionnaire.length === 1 ? 'question' : 'questions'}
          />

          <div data-tour-id="qas-table-area" className="p-0 min-h-[250px]">
            {renderCurrentTab()}
          </div>
        </div>
      )}
      <FeatureTour
        steps={qasItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default QAsTabContent;
